import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import {
    ISD_CODE,
    TENANT_ID,
    PRODUCT_ID,
    REACT_APP_MDS_API_URL,
    SCHOOL_API_BASE_URL,
    UDISE_SCHOOL_MGMT_API_URL,
    ONEGENIUS_API_BASE_URL,
    DEFAULT_BRANCH_PAGE_NUMBER,
    BRANCH_PAGE_SIZE_OPTIONS,
    ZERO,
} from 'constants/constants';
import {
    DEFAULT_SCHOOL_PAGE_NUMBER,
    DEFAULT_SCHOOL_PAGE_SIZE,
    INDIA_POSTAL_CODE_LENGTH,
    SCHOOL_API_CACHE_TIME_MS,
    SCHOOL_API_PATHS,
    SCHOOL_API_REQUEST_FIELDS,
    SCHOOL_MDS_RESOURCE_TYPES,
    SCHOOL_QUERY_KEYS,
    SCHOOL_API_HEADER_VALUES,
    UDISE_SCHOOL_API_REQUEST_FIELDS,
} from 'constants/schoolConstants';
import urlMapping from 'constants/urlMapping';
import fetchJSON from 'helpers/restApiClient';
import {
    buildSchoolApiHeaders,
    buildSchoolGatewayAgentHeaders,
    callSchoolApi,
    getExecutiveRoleIds,
    getSchoolPocRoleIds,
    normalizeAgentsResponse,
    normalizeCityMappingItem,
    normalizeCountryMappingItem,
    normalizeMdsMappingListResponse,
    normalizeTierMappingItem,
} from 'helpers/schoolUtils';
import { tokenUtils } from 'utils';



const fetchAgentsByUserIds = async (uamIds) => {
  const token = tokenUtils.getToken();
  const url = `${process.env.REACT_APP_LOGIN_GATEWAY_API_ENDPOINT}/api/v1/user/agents/fetch-agents-by-user-id`;
  const options = {
    method: 'POST',
    headers: buildSchoolGatewayAgentHeaders(token),
    body: {
      uam_ids: uamIds,
    },
  };

  return fetchJSON(url, options).then(normalizeAgentsResponse);
};

export const useFetchAgentsByUserIds = (uamIds) => {
  return useQuery({
    queryKey: [SCHOOL_QUERY_KEYS.AGENTS_BY_USER_IDS, uamIds],
    queryFn: () => fetchAgentsByUserIds(uamIds),
    enabled: !!uamIds && Array.isArray(uamIds) && uamIds.length > ZERO,
    staleTime: SCHOOL_API_CACHE_TIME_MS,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

const getLoginPayload = (response) => {
    if (response?.data?.accessToken) return response.data;
    if (!response?.accessToken) return null;
    const { statusCode, statusText, ...payload } = response;
    return payload;
};

const login = ({ mobile, password }) => {
    const options = {
        method: 'POST',
        headers: {
            platform: 'web',
            'product-id': '1000',
            'tenant-id': TENANT_ID,
        },
        body: {
            isd_code: ISD_CODE,
            phone: mobile,
            password,
            product_id: PRODUCT_ID,
            tenant_id: TENANT_ID,
        },
    };

    return fetchJSON(`${urlMapping.encryptedLogin}`, options)
        .then((response) => tokenUtils.login(getLoginPayload(response)));
};

export const useAuthentication = () => {
    return useMutation({
        mutationFn: login,
    });
};

const searchTenants = async ({ searchBy, searchTerm, page = 1, limit = 10 }) => {
    const token = tokenUtils.getToken();
    const params = new URLSearchParams({
        search_term: searchTerm,
        search_by: searchBy,
        page: String(page),
        limit: String(limit),
    });
    const url = `${process.env.REACT_APP_TENANT_MANAGEMENT_API_URL}/search-tenants?${params.toString()}`;

    const options = {
        method: 'GET',
        headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    };

    return fetchJSON(url, options);
};

export const useSearchTenants = () => {
    return useMutation({
        mutationFn: searchTenants,
    });
};


const bulkRegister = async (selectedFile) => {
    const token = tokenUtils.getToken();
    const url = `${process.env.REACT_APP_LOGIN_GATEWAY_API_ENDPOINT}/api/v1/unified/bulk-register`;

    const formData = new FormData();
    formData.append("fileobject", selectedFile);

    const options = {
        method: "POST",
        headers: {
            "accept": "application/json",
            "product-id": "100",
            "tenant-id": "99999",
            "platform": "web",
            "Authorization": `Bearer ${token}`,
        },
        body: formData,
    };

    return fetchJSON(url, options);
};

export const useBulkRegister = () => {
    return useMutation({
        mutationFn: bulkRegister,
    });
};

const uploadFileService = async ({ selectedFile, userId, scriptId }) => {
    const url = new URL(
      `${process.env.REACT_APP_UPLOAD_SERVICE_URL}/upload-file?uploaded_by=${userId}&selected_script_id=${scriptId}`
    );
    const formData = new FormData();
    formData.append("file", selectedFile);
    const options={
        method: "POST",
        headers: {
            accept: "application/json",
            "secret-key": `${process.env.REACT_APP_UPLOAD_SERVICE_SECRET_KEY}`,
        },
        body: formData,
    };
    return fetchJSON(url, options);
  };
  
  export const useUploadFileService = () => {
    return useMutation({ mutationFn: uploadFileService });
  };

  const fetchUploadedFileHistory = async ({ moduleId, offset = 0, limit = 10 }) => {
    const url = new URL(`${process.env.REACT_APP_UPLOAD_SERVICE_URL}/get-all-files`);
    url.searchParams.set("module_id", String(moduleId));
    url.searchParams.set("offset", String(offset));
    url.searchParams.set("limit", String(limit));

    const options = {
        method: "GET",
        headers: {
            accept: "application/json",
            "secret-key": `${process.env.REACT_APP_UPLOAD_SERVICE_SECRET_KEY}`,
        },
    };
    return fetchJSON(url, options);
  };
  
  export const useFetchUploadedFileHistory = ({ moduleId, offset = 0, limit = 10 }) => {
    return useQuery({
        queryKey: ["uploadedFileHistory", moduleId, offset, limit],
        queryFn: () => fetchUploadedFileHistory({ moduleId, offset, limit }),
        enabled: !!moduleId,
    });
  };

const fetchJobList = async () => {
    const token = tokenUtils.getToken();
    const url = `${process.env.REACT_APP_LOGIN_GATEWAY_API_ENDPOINT}/api/v1/unified/get-job-list`;
    const options = {
        method: "GET",
        headers: {
            "accept": "application/json",
            "product-id": "100",
            "tenant-id": "99999",
            "platform": "web",
            "Authorization": `Bearer ${token}`,
        }
    };
    return fetchJSON(url, options, { method: "GET" });
};

export const useJobList = () => {
    return useQuery({
        queryKey: ["jobList"],
        queryFn: fetchJobList,
    });
};

const fetchPostalCodeDetails = async (postalCode) => {
    const token = tokenUtils.getToken();
    const url = new URL(
        `${process.env.REACT_APP_ONE_OMS_URL}/get-postal-code-details/${postalCode}`
    );
    const options = {
        headers: {
            accept: "application/json",
            "auth-token": `Bearer ${token}`,
        }
    }
    return fetchJSON(url, options)
};

export const useFetchPostalCodeDetails = (postalCode) => {
    const isQueryEnabled =
        postalCode && postalCode.length === INDIA_POSTAL_CODE_LENGTH;
    return useQuery({
        queryKey: [SCHOOL_QUERY_KEYS.POSTAL_CODE_DETAILS, postalCode],
        queryFn: () => fetchPostalCodeDetails(postalCode),
        enabled: !!isQueryEnabled,
        staleTime: SCHOOL_API_CACHE_TIME_MS,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    });
};

export const fetchPostalCodeDetailsByPin = (queryClient, postalCode) =>
    queryClient.fetchQuery({
        queryKey: [SCHOOL_QUERY_KEYS.POSTAL_CODE_DETAILS, postalCode],
        queryFn: () => fetchPostalCodeDetails(postalCode),
        staleTime: SCHOOL_API_CACHE_TIME_MS,
    });

const createTenant = async (payload) => {
    const token = tokenUtils.getToken();
    const url = `${process.env.REACT_APP_TENANT_MANAGEMENT_API_URL}/create-tenant`;
    const options = {
        method: "POST",
        headers: {
            "accept": "application/json",
            "Authorization": `Bearer ${token}`,
        },
        body: payload,
    };

    return fetchJSON(url, options);
}
export const useCreateTenant = () => {
    return useMutation({
        mutationFn: createTenant,
    });
};
const searchBranch = async ({ tenantCode, branchCode }) => {
    const token = tokenUtils.getToken();
    const url = `${process.env.REACT_APP_TENANT_MANAGEMENT_API_URL}/get-school-code-details?tenant_code=${tenantCode}&branch_code=${branchCode}`;
    const options = {
        method: 'GET',
        headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`,

        }
    }
    return fetchJSON(url, options)
}

export const useSearchBranch = () => {
    return useMutation({
        mutationFn: searchBranch,
    })
}

const searchBranchesByName = async ({ branchName, page = DEFAULT_BRANCH_PAGE_NUMBER, limit = BRANCH_PAGE_SIZE_OPTIONS[0] }) => {
    const token = tokenUtils.getToken();
    const params = new URLSearchParams({
        name: branchName,
        page: String(page),
        limit: String(limit),
    });
    const url = `${process.env.REACT_APP_TENANT_MANAGEMENT_API_URL}/search-branches?${params.toString()}`;
    const options = {
        method: 'GET',
        headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    };

    return fetchJSON(url, options);
};

export const useSearchBranchesByName = () => {
    return useMutation({
        mutationFn: searchBranchesByName,
        staleTime: SCHOOL_API_CACHE_TIME_MS,
    });
};
const fetchTenantDetails = async (tenantId) => {
    const token = tokenUtils.getToken();
    const url = `${process.env.REACT_APP_TENANT_MANAGEMENT_API_URL}/get-tenant-info/${tenantId}`;
    const options = {
        method: "GET",
        headers: {
            "accept": "application/json",
            "Authorization": `Bearer ${token}`,
        },
    };

    return fetchJSON(url, options);
};
export const useFetchTenantDetails = (tenantId) => {
    return useQuery({
        queryKey: ["tenantDetails", tenantId],
        queryFn: () => fetchTenantDetails(tenantId),
        enabled: !!tenantId,
        refetchOnWindowFocus: false,
    });
};

const editTenantDetails = async (payload) => {
    const token = tokenUtils.getToken();
    const url = `${process.env.REACT_APP_TENANT_MANAGEMENT_API_URL}/update-tenant`;
    const options = {
        method: "PUT",
        headers: {
            "accept": "application/json",
            "Authorization": `Bearer ${token}`,
        },
        body: payload,
    };

    return fetchJSON(url, options);
}
export const useEditTenantDetails = () => {
    return useMutation({
        mutationFn: editTenantDetails,
    });
};
const createBranch = async (payload) => {
    const token = tokenUtils.getToken();
    const url = `${process.env.REACT_APP_TENANT_MANAGEMENT_API_URL}/create-branch`;
    const options = {
        method: "POST",
        headers: {
            "accept": "application/json",
            "Authorization": `Bearer ${token}`,
        },
        body: payload,
    };

    return fetchJSON(url, options);
}
export const useCreateBranch = () => {
    return useMutation({
        mutationFn: createBranch,
    });
};

const fetchBranchDetails = async (branchId) => {
    const token = tokenUtils.getToken();
    const url = `${process.env.REACT_APP_TENANT_MANAGEMENT_API_URL}/get-branch-info/${branchId}`;
    const options = {
        method: "GET",
        headers: {
            "accept": "application/json",
            "Authorization": `Bearer ${token}`,
        },
    };

    return fetchJSON(url, options);
};
export const useFetchBranchDetails = (branchId) => {
    return useQuery({
        queryKey: ["tenantDetails", branchId],
        queryFn: () => fetchBranchDetails(branchId),
        enabled: !!branchId,
        refetchOnWindowFocus: false,
    });
};
const editBranchDetails = async (payload) => {
    const token = tokenUtils.getToken();
    const url = `${process.env.REACT_APP_TENANT_MANAGEMENT_API_URL}/update-branch`;
    const options = {
        method: "PUT",
        headers: {
            "accept": "application/json",
            "Authorization": `Bearer ${token}`,
        },
        body: payload,
    };

    return fetchJSON(url, options);
}
export const useEditBranchDetails = () => {
    return useMutation({
        mutationFn: editBranchDetails,
    });
};
const fetchVerticalList = async () => {
    const url = `${process.env.REACT_APP_MDS_API_URL}/get-all-mappings?resource_type=vertical`;
    const options = {
        method: "POST",
        headers:{
            "accept": "application/json",
        }
    }
    return fetchJSON(url, options);
}
export const useFetchVerticalList = () => {
    return useQuery({
        queryKey: ["verticalList"],
        queryFn: fetchVerticalList,
    });
};
const fetchMdsMapping = (resourceType, normalizer) => async () => {
    const url = `${REACT_APP_MDS_API_URL}/get-all-mappings?resource_type=${resourceType}`;
    const options = { method: "POST", headers: { accept: "application/json" } };
    const res = await fetchJSON(url, options);
    return normalizer ? normalizeMdsMappingListResponse(res, normalizer) : res;
};

export const useFetchGradeList = () =>
    useQuery({
        queryKey: [SCHOOL_QUERY_KEYS.GRADE_LIST],
        queryFn: fetchMdsMapping(SCHOOL_MDS_RESOURCE_TYPES.GRADE),
        staleTime: SCHOOL_API_CACHE_TIME_MS,
    });

export const useFetchBoardList = () =>
    useQuery({
        queryKey: [SCHOOL_QUERY_KEYS.BOARD_LIST],
        queryFn: fetchMdsMapping(SCHOOL_MDS_RESOURCE_TYPES.BOARD),
        staleTime: SCHOOL_API_CACHE_TIME_MS,
    });

export const useFetchCountryList = (enabled = true) =>
    useQuery({
        queryKey: [SCHOOL_QUERY_KEYS.COUNTRY_LIST],
        queryFn: fetchMdsMapping(
            SCHOOL_MDS_RESOURCE_TYPES.COUNTRY,
            normalizeCountryMappingItem,
        ),
        enabled,
        staleTime: SCHOOL_API_CACHE_TIME_MS,
    });

export const useFetchCityList = () =>
    useQuery({
        queryKey: [SCHOOL_QUERY_KEYS.CITY_LIST],
        queryFn: fetchMdsMapping(
            SCHOOL_MDS_RESOURCE_TYPES.CITY,
            normalizeCityMappingItem,
        ),
        staleTime: SCHOOL_API_CACHE_TIME_MS,
    });

export const useFetchTierList = () =>
    useQuery({
        queryKey: [SCHOOL_QUERY_KEYS.TIER_LIST],
        queryFn: fetchMdsMapping(
            SCHOOL_MDS_RESOURCE_TYPES.CITY_TIER,
            normalizeTierMappingItem,
        ),
        staleTime: SCHOOL_API_CACHE_TIME_MS,
    });

const searchSchool = async ({
    udiseCode,
    schoolName,
    schoolCode,
    page = DEFAULT_SCHOOL_PAGE_NUMBER,
    pageSize = DEFAULT_SCHOOL_PAGE_SIZE,
}) => {
    const token = tokenUtils.getToken();
    const body = {
        ...(udiseCode && {
            [SCHOOL_API_REQUEST_FIELDS.UDISE_CODE]: udiseCode,
        }),
        ...(schoolName && {
            [SCHOOL_API_REQUEST_FIELDS.SCHOOL_NAME]: schoolName,
        }),
        ...(schoolCode && {
            [SCHOOL_API_REQUEST_FIELDS.SCHOOL_CODE]: schoolCode,
        }),
    };

    const url = new URL(`${SCHOOL_API_BASE_URL}${SCHOOL_API_PATHS.SEARCH}`);
    url.searchParams.set(SCHOOL_API_REQUEST_FIELDS.PAGE, Number(page));
    url.searchParams.set(SCHOOL_API_REQUEST_FIELDS.PAGE_SIZE, Number(pageSize));

    const options = {
        method: "POST",
        headers: buildSchoolApiHeaders(token),
        body,
    };

    return callSchoolApi(url, options);
};

const fetchSchoolDetails = async (schoolId) => {
    const token = tokenUtils.getToken();
    const url = new URL(`${SCHOOL_API_BASE_URL}${SCHOOL_API_PATHS.GET_DETAILS}`);
    url.searchParams.set(SCHOOL_API_REQUEST_FIELDS.SCHOOL_ID, schoolId);

    const options = {
        method: "GET",
        headers: buildSchoolApiHeaders(token),
    };

    return callSchoolApi(url, options);
};

const editSchoolDetails = async ({ schoolId, payload }) => {
    const token = tokenUtils.getToken();
    const options = {
        method: "POST",
        headers: buildSchoolApiHeaders(token),
        body: {
            [SCHOOL_API_REQUEST_FIELDS.SCHOOL_ID]: schoolId,
            ...payload,
        },
    };

    return callSchoolApi(`${SCHOOL_API_BASE_URL}${SCHOOL_API_PATHS.UPDATE}`, options);
};

const createSchool = async (payload) => {
    const token = tokenUtils.getToken();
    const options = {
        method: "POST",
        headers: buildSchoolApiHeaders(token),
        body: payload,
    };

    return callSchoolApi(`${SCHOOL_API_BASE_URL}${SCHOOL_API_PATHS.REGISTER}`, options);
};

export const useCreateSchool = () => {
    return useMutation({
        mutationFn: createSchool,
    });
};

const fetchUdiseSchoolByCode = async (udiseCode) => {
    const url = new URL(
        `${UDISE_SCHOOL_MGMT_API_URL}/v1/schools`,
    );
    url.searchParams.set(UDISE_SCHOOL_API_REQUEST_FIELDS.UDISE_CODE, udiseCode);
    return fetchJSON(url.toString(), {
        method: "GET",
        headers: {
            accept: SCHOOL_API_HEADER_VALUES.ACCEPT_JSON,
        },
    });
};

export const useFetchUdiseSchool = () => {
    return useMutation({
        mutationFn: fetchUdiseSchoolByCode,
        mutationKey: [SCHOOL_QUERY_KEYS.UDISE_SCHOOL],
    });
};

export const useEditSchoolDetails = () => {
    return useMutation({
        mutationFn: editSchoolDetails,
    });
};

export const useSearchSchools = () => {
    return useMutation({
        mutationFn: searchSchool,
        staleTime: SCHOOL_API_CACHE_TIME_MS,
    });
};

export const useFetchSchoolDetails = (schoolId) => {
    return useQuery({
        queryKey: [SCHOOL_QUERY_KEYS.DETAILS, schoolId],
        queryFn: () => fetchSchoolDetails(schoolId),
        enabled: !!schoolId,
        staleTime: SCHOOL_API_CACHE_TIME_MS,
        refetchOnWindowFocus: false,
    });
};

const fetchSchoolCategoryDetails = async (schoolCode) => {
    const token = tokenUtils.getToken();
    const url = `${ONEGENIUS_API_BASE_URL}/api/v1/admin/schools/${schoolCode}/category-details`;
    const options = {
        method: "GET",
        headers: {
            accept: SCHOOL_API_HEADER_VALUES.ACCEPT_JSON,
            Authorization: `Bearer ${token}`,
        },
    };

    return fetchJSON(url, options);
};

export const useFetchSchoolCategoryDetails = (schoolCode) => {
    return useQuery({
        queryKey: [SCHOOL_QUERY_KEYS.CATEGORY_DETAILS, schoolCode],
        queryFn: () => fetchSchoolCategoryDetails(schoolCode),
        enabled: !!schoolCode,
        staleTime: SCHOOL_API_CACHE_TIME_MS,
        refetchOnWindowFocus: false,
        retry: false,
    });
};

const uploadTenants = async (selectedFile) => {
    const token = tokenUtils.getToken();
    const url = `${process.env.REACT_APP_TENANT_MANAGEMENT_API_URL}/upload-tenants`;

    const formData = new FormData();
    formData.append("file", selectedFile);

    const options = {
        method: "POST",
        headers: {
            "accept": "application/json",
            "product-id": "100",
            "tenant-id": "99999",
            "platform": "web",
            "Authorization": `Bearer ${token}`,
        },
        body: formData,
    };

    return fetchJSON(url, options);
};

export const useUploadTenants = () => {
    return useMutation({
        mutationFn: uploadTenants,
    });
};

const uploadBranches = async (selectedFile) => {
    const token = tokenUtils.getToken();
    const url = `${process.env.REACT_APP_TENANT_MANAGEMENT_API_URL}/upload-branches`;

    const formData = new FormData();
    formData.append("file", selectedFile);

    const options = {
        method: "POST",
        headers: {
            "accept": "application/json",
            "product-id": "100",
            "tenant-id": "99999",
            "platform": "web",
            "Authorization": `Bearer ${token}`,
        },
        body: formData,
    };

    return fetchJSON(url, options);
};

export const useUploadBranches = () => {
    return useMutation({
        mutationFn: uploadBranches,
    });
};

const fetchUploadHistory = async (loggedInUserId) => {
    const token = tokenUtils.getToken();
    const url = `${process.env.REACT_APP_TENANT_MANAGEMENT_API_URL}/upload-history?crn_id=${loggedInUserId}&limit=10`;
    const options = {
        method: "GET",
        headers: {
            "accept": "application/json",
            "Authorization": `Bearer ${token}`,
        },
    };

    return fetchJSON(url, options);
};
export const useFetchUploadHistory = (loggedInUserId) => {
    return useQuery({
        queryKey: ["loggedInUserId"],
        queryFn: () => fetchUploadHistory(loggedInUserId),
        enabled: !!loggedInUserId,
    });
};

const fetchProcessedFile = async (uploadId) => {
    const token = tokenUtils.getToken();
    const url = `${process.env.REACT_APP_TENANT_MANAGEMENT_API_URL}/get-signed-url/${uploadId}`;
    const options = {
        method: "GET",
        headers: {
            "accept": "application/json",
            "Authorization": `Bearer ${token}`,
        },
    };

    return fetchJSON(url, options);
};
export const useFetchProcessedFile = () => {
    return useMutation({
        mutationFn: fetchProcessedFile,
    });
};


const fetchProcessedFileUploadService = async (fileId) => {
    const url = new URL(
      `${process.env.REACT_APP_UPLOAD_SERVICE_URL}/download-processed-file/${fileId}`
    );
    const options = {
      method: "GET",
      headers: {
        accept: "application/json",
        "secret-key": process.env.REACT_APP_UPLOAD_SERVICE_SECRET_KEY,
        "Content-Type": "application/json",
      },
    };
    const response = await fetchJSON(url, options);
    return response.data;
};

export const useFetchProcessedFileUploadService = () => {

    return useMutation({
        mutationFn: (fileId) => fetchProcessedFileUploadService(fileId),
    });
};
        
const searchAgents = async ({ name, roles }) => {
    const token = tokenUtils.getToken();
    const url = `${process.env.REACT_APP_LOGIN_GATEWAY_API_ENDPOINT}/api/v1/user/agents/search-agent`;
    const options = {
        method: "POST",
        headers: buildSchoolGatewayAgentHeaders(token),
        body: {
            name,
            roles,
        },
    };
    return fetchJSON(url, options).then(normalizeAgentsResponse);
};

const useSearchAgentsByRoles = (name, queryKey, roles) => {
    return useQuery({
        queryKey: [queryKey, name],
        queryFn: () => searchAgents({ name, roles }),
        enabled: Boolean(name),
        placeholderData: keepPreviousData,
        staleTime: SCHOOL_API_CACHE_TIME_MS,
        refetchOnWindowFocus: false,
    });
};

export const useSearchSchoolPocAgents = (name) =>
    useSearchAgentsByRoles(
        name,
        SCHOOL_QUERY_KEYS.SEARCH_POC_AGENTS,
        getSchoolPocRoleIds(),
    );

export const useSearchExecutiveAgents = (name) =>
    useSearchAgentsByRoles(
        name,
        SCHOOL_QUERY_KEYS.SEARCH_EXECUTIVE_AGENTS,
        getExecutiveRoleIds(),
    );
